import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";
import { pollWhileSyncing, useSnapshotSectionTiming } from "@/hooks/githubSnapshot";
import { type NamedOrg, namedOrgKey, orgApiPath } from "@/lib/named-org";
const RepoPermissionsSnapshot = z.object({
  synced_at: z.string().nullable(),
  attempted_at: z.string().nullable(),
  failure: z.string().nullable(),
  row_count: z.number()
});
const QUERY_KEY = "repo-permissions-snapshot";

/**
 * Keeps the page honest while the user's repository permissions for `org` are still being written by their first
 * sync. Listings answer the rows landed so far, so this polls the snapshot and refetches the page's queries as more
 * rows land.
 */
export function useRepoPermissionsSync(org) {
  const $ = _c(14);
  const api = useApi();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== org) {
    t0 = namedOrgKey(org);
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const orgKey = t0;
  let t1;
  if ($[2] !== orgKey) {
    t1 = [QUERY_KEY, orgKey];
    $[2] = orgKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== api || $[5] !== org) {
    t2 = async () => {
      const response = await api.get(`${orgApiPath(org)}/repository-permissions/snapshot`);
      return {
        snapshot: RepoPermissionsSnapshot.parse(response.data)
      };
    };
    $[4] = api;
    $[5] = org;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = org !== null;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      enabled: t3,
      staleTime: Number.POSITIVE_INFINITY,
      refetchInterval: _temp
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: data_0,
    status
  } = useQuery(t1, t2, t4);
  useSnapshotSectionTiming("repo_permissions.snapshot", status, data_0?.snapshot ?? null);
  const progress = data_0 ? `${data_0.snapshot.row_count}:${data_0.snapshot.synced_at}` : undefined;
  const seen = useRef(null);
  let t5;
  let t6;
  if ($[9] !== orgKey || $[10] !== progress || $[11] !== queryClient) {
    t5 = () => {
      if (progress === undefined) {
        return;
      }
      const previous = seen.current;
      seen.current = {
        orgKey,
        progress
      };
      if (previous !== null && previous.orgKey === orgKey && previous.progress !== progress) {
        queryClient.invalidateQueries({
          predicate: _temp2
        });
      }
    };
    t6 = [orgKey, progress, queryClient];
    $[9] = orgKey;
    $[10] = progress;
    $[11] = queryClient;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
}
function _temp2(query) {
  return query.queryKey[0] !== QUERY_KEY;
}
function _temp(data) {
  return pollWhileSyncing(data);
}